<template>
  <div class="department-employees-list">
    <!-- Filters -->
    <b-row class="mb-2">
      <b-col
        cols="12"
        md="6"
      >
        <b-input-group>
          <b-form-input
            v-model="filters.search"
            placeholder="Search employees..."
            @keyup.enter="loadEmployees"
          />
          <b-input-group-append>
            <b-button
              variant="primary"
              @click="loadEmployees"
            >
              <feather-icon icon="SearchIcon" />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col
        cols="12"
        md="3"
      >
        <v-select
          v-model="filters.status"
          :options="statusOptions"
          placeholder="All Statuses"
          clearable
          @input="loadEmployees"
        />
      </b-col>
    </b-row>

    <!-- Table -->
    <b-table
      :items="employees"
      :fields="tableColumns"
      :busy="loading"
      responsive
      striped
      hover
      show-empty
      empty-text="No employees found in this department"
      empty-filtered-text="No employees match your current filter"
    >
      <!-- Loading -->
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle mr-1" />
          <strong>Loading...</strong>
        </div>
      </template>

      <!-- Employee Code -->
      <template #cell(employee_code)="data">
        <b-badge
          variant="light-secondary"
          class="text-monospace"
        >
          {{ data.item.employee_code }}
        </b-badge>
      </template>

      <!-- Name -->
      <template #cell(full_name)="data">
        <b-link :to="{ name: 'employees-view', params: { id: data.item.id } }">
          {{ data.item.full_name }}
        </b-link>
      </template>

      <!-- Status -->
      <template #cell(status)="data">
        <b-badge
          :variant="getStatusVariant(data.item.status)"
        >
          {{ data.item.status }}
        </b-badge>
      </template>

      <!-- Contact -->
      <template #cell(contact)="data">
        <div v-if="data.item.primary_phone">
          {{ data.item.primary_phone.phone_number }}
        </div>
      </template>
    </b-table>

    <!-- Pagination -->
    <b-pagination
      v-if="pagination.total > pagination.perPage"
      v-model="pagination.currentPage"
      :total-rows="pagination.total"
      :per-page="pagination.perPage"
      align="center"
      @change="handlePageChange"
    />

    <!-- Fallback message when no employees -->
    <div
      v-if="!loading && employees.length === 0"
      class="text-center py-4"
    >
      <feather-icon
        icon="UsersIcon"
        size="48"
        class="text-muted mb-2"
      />
      <h5 class="text-muted">
        No Employees Found
      </h5>
      <p class="text-muted">
        This department doesn't have any employees assigned yet.
      </p>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BTable,
  BBadge,
  BLink,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapActions } from 'vuex'

export default {
  name: 'DepartmentEmployeesList',

  components: {
    BRow,
    BCol,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BTable,
    BBadge,
    BLink,
    BSpinner,
    BPagination,
    vSelect,
  },

  props: {
    departmentId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      employees: [],
      loading: false,
      filters: {
        search: '',
        status: null,
      },
      pagination: {
        currentPage: 1,
        perPage: 15,
        total: 0,
      },
      tableColumns: [
        { key: 'employee_code', label: 'Code' },
        { key: 'full_name', label: 'Name' },
        { key: 'status', label: 'Status' },
        { key: 'contact', label: 'Contact' },
      ],
      statusOptions: ['training', 'contracted', 'terminated'],
    }
  },

  watch: {
    departmentId: {
      handler(newId, oldId) {
        // Only load when ID is available and has actually changed
        if (newId && newId !== oldId) {
          console.log('Department ID watcher triggered - old:', oldId, 'new:', newId)
          this.loadEmployees()
        }
      },
      immediate: true, // This will call handler on mount with (newId, undefined)
    },
  },

  methods: {
    ...mapActions('departments', ['fetchDepartmentEmployees']),

    async loadEmployees() {
      // Don't load if no department ID
      if (!this.departmentId) {
        console.log('No department ID provided, skipping employee load')
        return
      }

      // Prevent multiple simultaneous calls for the same department
      if (this.loading) {
        console.log('Already loading employees, skipping...')
        return
      }

      this.loading = true
      console.log('=== START: Loading employees for department:', this.departmentId, '===')

      try {
        const response = await this.fetchDepartmentEmployees({
          id: this.departmentId,
          params: {
            per_page: this.pagination.perPage,
            page: this.pagination.currentPage,
            search: this.filters.search || undefined,
            status: this.filters.status || undefined,
          },
        })
        console.log('=== SUCCESS: Employees loaded:', response.data?.length || 0, 'items ===')
        this.employees = response.data || []
        if (response.meta) {
          this.pagination.total = response.meta.total
        }
      } catch (error) {
        console.error('=== ERROR: Failed to load employees ===', error)
        this.employees = []
        this.pagination.total = 0
        this.$emit('error', error)
      } finally {
        this.loading = false
        console.log('=== END: Employee loading complete ===')
      }
    },

    handlePageChange(page) {
      this.pagination.currentPage = page
      this.loadEmployees()
    },

    getStatusVariant(status) {
      const variants = {
        training: 'light-warning',
        contracted: 'light-success',
        terminated: 'light-danger',
      }
      return variants[status] || 'light-secondary'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';
</style>
